import { Grid } from '../../core/Grid.js';
import {
    Position,
    positionKey,
    parsePositionKey,
} from '../../core/GridGeometry.js';
import { CrossingGame, BallDirection, BallPath } from './CrossingGame.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class CrossingVortex extends CrossingGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.computeState();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            if (this.hasTeleporter(p)) return false;
            if (this.hasTeleporter(paired)) return false;
            if (this.grid.hasDigitAt(p)) return false;
            if (this.grid.hasDigitAt(paired)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.computeState();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Placement Blocking ────────────────────────────────────────

    override isValidTeleporterPosition(pos: Position): boolean {
        if (this.gm.isActiveGate(pos)) return false;
        return super.isValidTeleporterPosition(pos);
    }

    // ── Paired Teleporter (includes vortex gates for renderer) ───

    override getPairedTeleporter(pos: Position): Position | null {
        const playerPaired = super.getPairedTeleporter(pos);
        if (playerPaired) return playerPaired;

        const vortexPaired = this.gm.raw.get(positionKey(pos));
        if (vortexPaired) return parsePositionKey(vortexPaired);

        return null;
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Ball Teleportation ────────────────────────────────────────

    override simulateBallPath(startPos: Position, direction: BallDirection): BallPath {
        // Guard: during super() constructor, gm isn't initialized yet
        if (!this.gm || this.gm.raw.size === 0) {
            return super.simulateBallPath(startPos, direction);
        }

        const positions: Position[] = [startPos];
        const digitsHit: number[] = [];
        const startDigit = this.grid.getDigitAt(startPos);
        if (startDigit !== null) {
            digitsHit.push(startDigit);
        }

        let current = { ...startPos };
        const maxSteps = this.grid.size * this.grid.size * 2;
        let justVortexTeleported = false;

        for (let step = 0; step < maxSteps; step++) {
            // Move one step
            let next: Position;
            if (direction === 'down') {
                next = { row: (current.row + 1) % this.grid.size, col: current.col };
            } else {
                next = { row: current.row, col: (current.col + 1) % this.grid.size };
            }

            // Check for player teleporter at next position
            const playerPaired = this.teleporterPairs.get(positionKey(next));
            if (playerPaired) {
                positions.push(next);
                next = { ...playerPaired };
                justVortexTeleported = false;
            }

            // Check for vortex gate teleport (skip if just arrived via vortex teleport)
            if (!justVortexTeleported) {
                const vortexPaired = this.gm.raw.get(positionKey(next));
                if (vortexPaired) {
                    positions.push(next);
                    next = parsePositionKey(vortexPaired);
                    justVortexTeleported = true;
                } else {
                    justVortexTeleported = false;
                }
            } else {
                justVortexTeleported = false;
            }

            positions.push(next);
            current = next;

            // Check for digit at current position
            const digit = this.grid.getDigitAt(current);
            if (digit !== null) {
                digitsHit.push(digit);
                break;
            }

            // Check if we've returned to start
            if (current.row === startPos.row && current.col === startPos.col) {
                break;
            }
        }

        return { positions, digitsHit };
    }

    protected override simulateFullPath(startPos: Position, direction: BallDirection): Position[] {
        if (!this.gm || this.gm.raw.size === 0) {
            return super.simulateFullPath(startPos, direction);
        }

        const positions: Position[] = [{ ...startPos }];
        let current = { ...startPos };
        const maxSteps = this.grid.size * this.grid.size * 2;
        let justVortexTeleported = false;

        for (let step = 0; step < maxSteps; step++) {
            let next: Position;
            if (direction === 'down') {
                next = { row: (current.row + 1) % this.grid.size, col: current.col };
            } else {
                next = { row: current.row, col: (current.col + 1) % this.grid.size };
            }

            // Check for player teleporter
            const playerPaired = this.teleporterPairs.get(positionKey(next));
            if (playerPaired) {
                positions.push(next);
                next = { ...playerPaired };
                justVortexTeleported = false;
            }

            // Check for vortex gate teleport
            if (!justVortexTeleported) {
                const vortexPaired = this.gm.raw.get(positionKey(next));
                if (vortexPaired) {
                    positions.push(next);
                    next = parsePositionKey(vortexPaired);
                    justVortexTeleported = true;
                } else {
                    justVortexTeleported = false;
                }
            } else {
                justVortexTeleported = false;
            }

            positions.push(next);
            current = next;

            if (current.row === startPos.row && current.col === startPos.col) {
                break;
            }
        }

        return positions;
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const pairCount = this.teleporterPairs.size / 2;

        const overlapCells = new Set<string>();
        for (const pos of this.getOverlapCells()) {
            overlapCells.add(positionKey(pos));
        }

        return calculateScore(pairCount, this.gm.deduplicateCellCount(overlapCells));
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.computeState();
    }
}
